import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigation } from '../../../shared/contexts/NavigationContext';
import './BottomSheet.css';

export type BottomSheetSize = 'small' | 'medium' | 'large' | 'fullscreen' | 'auto';

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** Дополнительный класс для заголовка (например, цвет предупреждения) */
  titleClassName?: string;
  children: React.ReactNode;
  size?: BottomSheetSize;
  showHandle?: boolean;
  allowSwipeToClose?: boolean;
  allowSwipeToExpand?: boolean;
  className?: string;
  /** Повышенный z-index — для вложенных боттомшитов (профиль и т.п.), чтобы отображаться поверх родительского */
  elevated?: boolean;
}

// Утилита для throttle
const throttle = <T extends (...args: any[]) => void>(func: T, limit: number): T => {
  let inThrottle: boolean;
  return ((...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  }) as T;
};

// Утилита для безопасного парсинга translateY
const parseTranslateY = (transform: string): number => {
  if (!transform || transform === 'none') return 0;
  const match = transform.match(/translateY\((-?\d+(?:\.\d+)?)px\)/);
  return match ? parseFloat(match[1]) : 0;
};

/** Доля экрана: выше — открываем на весь экран */
const VIEWPORT_MAX_RATIO = 0.92;
/** Вертикальные отступы .bottom-sheet-content (padding сверху/снизу) */
const CONTENT_VERTICAL_PADDING = 40;

const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  titleClassName,
  children,
  size = 'auto',
  showHandle = true,
  allowSwipeToClose = true,
  allowSwipeToExpand = true,
  className = '',
  elevated = false
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const currentSizeRef = useRef<BottomSheetSize>(size);
  const [isClosing, setIsClosing] = useState(false);
  const [effectiveSize, setEffectiveSize] = useState<BottomSheetSize>(size);
  const [fittedHeightPx, setFittedHeightPx] = useState<number | null>(null);
  const initialHeightRef = useRef<number>(0);
  const openedAtRef = useRef<number>(0);
  const { pushBackLayer, popBackLayer } = useNavigation();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const displaySize: BottomSheetSize = size === 'auto' ? effectiveSize : size;
  const isFittedAuto = size === 'auto' && displaySize === 'auto' && fittedHeightPx != null;

  // Размеры для разных состояний
  const sizeMap = {
    small: '33vh',
    medium: '50vh', 
    large: '75vh',
    fullscreen: '100vh',
    auto: 'auto'
  };

  // Сброс размера при изменении prop size
  useEffect(() => {
    currentSizeRef.current = size === 'auto' ? effectiveSize : size;
  }, [size, effectiveSize]);

  useEffect(() => {
    if (!isOpen) {
      setEffectiveSize(size);
      setFittedHeightPx(null);
      currentSizeRef.current = size;
    }
  }, [isOpen, size]);

  const measureAutoLayout = useCallback(() => {
    if (size !== 'auto' || !isOpen || !sheetRef.current || !contentRef.current) return;

    const sheet = sheetRef.current;
    const content = contentRef.current;
    const maxSheetHeight = Math.floor(window.innerHeight * VIEWPORT_MAX_RATIO);

    const handleEl = sheet.querySelector('.bottom-sheet-handle');
    const headerEl = sheet.querySelector('.bottom-sheet-header');
    const chromeHeight =
      (showHandle && handleEl ? handleEl.getBoundingClientRect().height : 0) +
      (title && headerEl ? headerEl.getBoundingClientRect().height : 0) +
      CONTENT_VERTICAL_PADDING;

    const contentHeight = content.scrollHeight;
    const neededHeight = chromeHeight + contentHeight;

    if (neededHeight >= maxSheetHeight) {
      setEffectiveSize('fullscreen');
      setFittedHeightPx(null);
      currentSizeRef.current = 'fullscreen';
    } else {
      setEffectiveSize('auto');
      setFittedHeightPx(Math.ceil(neededHeight));
      currentSizeRef.current = 'auto';
    }
  }, [size, isOpen, title, showHandle]);

  useLayoutEffect(() => {
    if (!isOpen || size !== 'auto') return;

    measureAutoLayout();

    const content = contentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      measureAutoLayout();
    });
    observer.observe(content);

    const onResize = () => measureAutoLayout();
    window.addEventListener('resize', onResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, size, measureAutoLayout, children]);

  // Время открытия — для игнора «призрачного» тапа по бэкдропу на мобильных (тап по «Профиль» → меню закрылось → профиль открылся → тот же тап «попадает» в бэкдроп)
  useEffect(() => {
    if (isOpen) openedAtRef.current = Date.now();
  }, [isOpen]);

  // Инициализация начальной высоты при открытии
  useEffect(() => {
    if (isOpen && sheetRef.current) {
      const timeoutId = setTimeout(() => {
        if (sheetRef.current) {
          const rect = sheetRef.current.getBoundingClientRect();
          initialHeightRef.current = rect.height || sheetRef.current.offsetHeight;
          // Если высота все еще 0, пробуем еще раз через небольшую задержку
          if (initialHeightRef.current === 0) {
            setTimeout(() => {
              if (sheetRef.current) {
                const rect2 = sheetRef.current.getBoundingClientRect();
                initialHeightRef.current = rect2.height || sheetRef.current.offsetHeight;
              }
            }, 50);
          }
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    } else {
      initialHeightRef.current = 0;
    }
  }, [isOpen]);

  // Закрытие модалки: анимация закрытия (slideOutDown + fadeOut), затем onClose
  const handleClose = useCallback(() => {
    setIsClosing(true);
    // Не сбрасываем стили панели — анимация .bottom-sheet.closing сама уводит панель вниз
    const closeDelay = 200; // совпадает с длительностью анимаций в CSS
    setTimeout(() => {
      if (sheetRef.current) {
        sheetRef.current.style.height = '';
        sheetRef.current.style.maxHeight = '';
        sheetRef.current.style.transform = '';
      }
      onClose();
      setIsClosing(false);
      currentSizeRef.current = size;
      setEffectiveSize(size);
      setFittedHeightPx(null);
      initialHeightRef.current = 0;
    }, closeDelay);
  }, [onClose, size]);

  // Блокировка прокрутки body (useLayoutEffect для синхронности)
  useLayoutEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Управление кнопкой "Назад" - используем стек слоёв для правильного восстановления
  // предыдущего обработчика при закрытии BottomSheet
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleBackForSheet = () => {
      onCloseRef.current();
    };

    // Добавляем обработчик в стек вместо перезаписи
    pushBackLayer(handleBackForSheet, false);

    return () => {
      // Убираем наш обработчик из стека, автоматически восстанавливая предыдущий
      popBackLayer();
    };
  }, [isOpen, pushBackLayer, popBackLayer]);

  // Обработка Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClose]);

  const BACKDROP_CLICK_GRACE_MS = 400;

  // Обработка клика по backdrop
  const handleBackdropClick = useCallback((event: React.MouseEvent) => {
    if (event.target !== backdropRef.current) return;
    if (Date.now() - openedAtRef.current < BACKDROP_CLICK_GRACE_MS) return;
    handleClose();
  }, [handleClose]);

  // Проверка, можно ли начинать свайп для закрытия
  const canSwipeToClose = useCallback((): boolean => {
    if (!contentRef.current) return true;
    
    // Проверяем, прокручен ли контент
    const scrollTop = contentRef.current.scrollTop;
    
    // Разрешаем свайп только если контент в самом верху
    return scrollTop <= 0;
  }, []);

  // Обработка начала перетаскивания (только на handle и header)
  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    if (!allowSwipeToClose && !allowSwipeToExpand) return;
    
    if (sheetRef.current) {
      // Используем getBoundingClientRect для более точного определения высоты
      const rect = sheetRef.current.getBoundingClientRect();
      initialHeightRef.current = rect.height || sheetRef.current.offsetHeight;
      // Если высота не установлена, используем текущую высоту из offsetHeight
      if (initialHeightRef.current === 0) {
        initialHeightRef.current = sheetRef.current.offsetHeight;
      }
    }
    setIsDragging(true);
    setDragStartY(event.touches[0].clientY);
  }, [allowSwipeToClose, allowSwipeToExpand]);

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (!allowSwipeToClose && !allowSwipeToExpand) return;
    
    if (sheetRef.current) {
      // Используем getBoundingClientRect для более точного определения высоты
      const rect = sheetRef.current.getBoundingClientRect();
      initialHeightRef.current = rect.height || sheetRef.current.offsetHeight;
      // Если высота не установлена, используем текущую высоту из offsetHeight
      if (initialHeightRef.current === 0) {
        initialHeightRef.current = sheetRef.current.offsetHeight;
      }
    }
    setIsDragging(true);
    setDragStartY(event.clientY);
  }, [allowSwipeToClose, allowSwipeToExpand]);

  // Общая логика обработки свайпа
  const processSwipeMove = useCallback((deltaY: number) => {
    if (!sheetRef.current) return false;

    const windowHeight = window.innerHeight;
    const isFullscreen = currentSizeRef.current === 'fullscreen';
    const isAuto = size === 'auto' && currentSizeRef.current === 'auto';
    
    // Свайп вниз для закрытия
    if (deltaY > 0 && allowSwipeToClose && canSwipeToClose()) {
      const sheetHeight = sheetRef.current.offsetHeight;
      // При свайпе вниз используем translateY для закрытия
      sheetRef.current.style.transform = `translateY(${Math.min(deltaY, sheetHeight)}px)`;
      return true; // Сигнал что нужно preventDefault
    } 
    // Свайп вверх для расширения (не применимо для auto)
    else if (deltaY < 0 && allowSwipeToExpand && !isFullscreen && !isAuto) {
      // Проверяем, что начальная высота установлена
      if (initialHeightRef.current === 0) {
        const rect = sheetRef.current.getBoundingClientRect();
        initialHeightRef.current = rect.height || sheetRef.current.offsetHeight;
      }
      
      // Проверяем, что есть место для растягивания
      if (initialHeightRef.current >= windowHeight * 0.95) {
        // Уже почти на максимуме, не растягиваем
        return false;
      }
      
      // При свайпе вверх увеличиваем высоту компонента
      const expandAmount = Math.abs(deltaY);
      const currentHeight = sheetRef.current.offsetHeight || initialHeightRef.current;
      const newHeight = Math.min(Math.max(currentHeight, initialHeightRef.current) + expandAmount, windowHeight);
      
      // Устанавливаем новую высоту вместо translateY
      sheetRef.current.style.height = `${newHeight}px`;
      sheetRef.current.style.maxHeight = `${windowHeight}px`; // Убираем ограничение max-height
      sheetRef.current.style.transform = 'translateY(0)';
      
      return true; // Сигнал что нужно preventDefault
    }
    
    return false;
  }, [allowSwipeToClose, allowSwipeToExpand, canSwipeToClose, size]);

  // Обработка перетаскивания (используем нативные события для preventDefault)
  const handleTouchMove = useCallback(
    throttle((event: TouchEvent) => {
      if (!isDragging) return;
      
      const currentY = event.touches[0].clientY;
      const deltaY = currentY - dragStartY;
      
      const shouldPreventDefault = processSwipeMove(deltaY);
      
      // Предотвращаем прокрутку только если свайп обрабатывается
      if (shouldPreventDefault && event.cancelable) {
        event.preventDefault();
      }
    }, 16), // ~60fps
    [isDragging, dragStartY, processSwipeMove]
  );

  const handleMouseMove = useCallback(
    throttle((event: MouseEvent) => {
      if (!isDragging) return;
      
      event.preventDefault();
      const currentY = event.clientY;
      const deltaY = currentY - dragStartY;
      
      processSwipeMove(deltaY);
    }, 16), // ~60fps
    [isDragging, dragStartY, processSwipeMove]
  );

  // Обработка окончания перетаскивания
  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    if (sheetRef.current) {
      const transform = sheetRef.current.style.transform;
      const translateY = parseTranslateY(transform);
      const currentHeight = sheetRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      const isFullscreen = currentSizeRef.current === 'fullscreen';
      const isAuto = size === 'auto' && currentSizeRef.current === 'auto';
      
      // Обработка закрытия (свайп вниз)
      if (translateY > 100 && allowSwipeToClose) {
        handleClose();
        return;
      }
      
      // Обработка расширения (свайп вверх)
      if (translateY === 0 && allowSwipeToExpand && !isFullscreen && !isAuto) {
        const heightIncrease = currentHeight - initialHeightRef.current;
        
        // Если увеличили высоту достаточно (больше 100px), переходим в fullscreen
        if (heightIncrease > 100 || currentHeight >= windowHeight * 0.9) {
          sheetRef.current.style.height = '100vh';
          sheetRef.current.style.maxHeight = '100vh';
          sheetRef.current.style.transform = 'translateY(0)';
          currentSizeRef.current = 'fullscreen';
        } else if (heightIncrease > 20) {
          // Если увеличили немного, оставляем увеличенную высоту
          // Сохраняем текущую высоту и убираем ограничение max-height
          sheetRef.current.style.maxHeight = `${windowHeight}px`;
          sheetRef.current.style.transform = 'translateY(0)';
        } else {
          // Возвращаем к исходной высоте
          sheetRef.current.style.height = '';
          sheetRef.current.style.maxHeight = '';
          sheetRef.current.style.transform = 'translateY(0)';
        }
      } else if (translateY > 50 && isFullscreen) {
        // Возврат из fullscreen к исходному размеру
        sheetRef.current.style.height = '';
        sheetRef.current.style.maxHeight = '';
        sheetRef.current.style.transform = 'translateY(0)';
        currentSizeRef.current = size;
      } else {
        // Вернуть в исходное положение
        sheetRef.current.style.transform = 'translateY(0)';
        if (!isAuto && translateY === 0) {
          sheetRef.current.style.height = ''; // Сброс высоты только если не auto и не было расширения
          sheetRef.current.style.maxHeight = ''; // Сброс max-height
        }
      }
    }
  }, [isDragging, allowSwipeToClose, allowSwipeToExpand, handleClose, size]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    if (sheetRef.current) {
      const transform = sheetRef.current.style.transform;
      const translateY = parseTranslateY(transform);
      const currentHeight = sheetRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      const isFullscreen = currentSizeRef.current === 'fullscreen';
      const isAuto = size === 'auto' && currentSizeRef.current === 'auto';
      
      // Обработка закрытия (свайп вниз)
      if (translateY > 100 && allowSwipeToClose) {
        handleClose();
        return;
      }
      
      // Обработка расширения (свайп вверх)
      if (translateY === 0 && allowSwipeToExpand && !isFullscreen && !isAuto) {
        const heightIncrease = currentHeight - initialHeightRef.current;
        
        // Если увеличили высоту достаточно (больше 100px), переходим в fullscreen
        if (heightIncrease > 100 || currentHeight >= windowHeight * 0.9) {
          sheetRef.current.style.height = '100vh';
          sheetRef.current.style.maxHeight = '100vh';
          sheetRef.current.style.transform = 'translateY(0)';
          currentSizeRef.current = 'fullscreen';
        } else if (heightIncrease > 20) {
          // Если увеличили немного, оставляем увеличенную высоту
          // Сохраняем текущую высоту и убираем ограничение max-height
          sheetRef.current.style.maxHeight = `${windowHeight}px`;
          sheetRef.current.style.transform = 'translateY(0)';
        } else {
          // Возвращаем к исходной высоте
          sheetRef.current.style.height = '';
          sheetRef.current.style.maxHeight = '';
          sheetRef.current.style.transform = 'translateY(0)';
        }
      } else if (translateY > 50 && isFullscreen) {
        // Возврат из fullscreen к исходному размеру
        sheetRef.current.style.height = '';
        sheetRef.current.style.maxHeight = '';
        sheetRef.current.style.transform = 'translateY(0)';
        currentSizeRef.current = size;
      } else {
        // Вернуть в исходное положение
        sheetRef.current.style.transform = 'translateY(0)';
        if (!isAuto && translateY === 0) {
          sheetRef.current.style.height = ''; // Сброс высоты только если не auto и не было расширения
          sheetRef.current.style.maxHeight = ''; // Сброс max-height
        }
      }
    }
  }, [isDragging, allowSwipeToClose, allowSwipeToExpand, handleClose, size]);

  // Добавляем нативные обработчики событий
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  if (!isOpen) return null;

  const sheetInlineStyle: React.CSSProperties = {
    transform: isClosing ? undefined : (isDragging ? undefined : 'translateY(0)'),
  };

  if (displaySize === 'fullscreen') {
    sheetInlineStyle.height = sizeMap.fullscreen;
    sheetInlineStyle.maxHeight = sizeMap.fullscreen;
  } else if (isFittedAuto && fittedHeightPx != null) {
    sheetInlineStyle.height = `${fittedHeightPx}px`;
    sheetInlineStyle.maxHeight = `${Math.floor(window.innerHeight * VIEWPORT_MAX_RATIO)}px`;
  } else if (displaySize === 'auto') {
    sheetInlineStyle.height = sizeMap.auto;
    sheetInlineStyle.maxHeight = '90vh';
  } else if (['small', 'medium', 'large'].includes(displaySize)) {
    sheetInlineStyle.height = sizeMap[displaySize as 'small' | 'medium' | 'large'];
    sheetInlineStyle.maxHeight = sizeMap[displaySize as 'small' | 'medium' | 'large'];
  }

  return createPortal(
    <div 
      className={`bottom-sheet-backdrop ${isClosing ? 'closing' : ''} ${elevated ? 'bottom-sheet-elevated' : ''}`}
      ref={backdropRef}
      onClick={handleBackdropClick}
    >
      <div
        ref={sheetRef}
        className={[
          'bottom-sheet',
          displaySize,
          isFittedAuto ? 'bottom-sheet-fitted' : '',
          isDragging ? 'dragging' : '',
          isClosing ? 'closing' : '',
          elevated ? 'bottom-sheet-elevated' : '',
          className,
        ].filter(Boolean).join(' ')}
        style={sheetInlineStyle}
      >
        {/* Handle (ручка) */}
        {showHandle && (
          <div 
            className="bottom-sheet-handle"
            onTouchStart={handleTouchStart}
            onMouseDown={handleMouseDown}
          />
        )}

        {/* Заголовок */}
        {title && (
          <div 
            className="bottom-sheet-header"
            onTouchStart={handleTouchStart}
            onMouseDown={handleMouseDown}
          >
            <h3 className={['bottom-sheet-title', titleClassName].filter(Boolean).join(' ')}>{title}</h3>
            <button 
              className="bottom-sheet-close"
              onClick={handleClose}
              onTouchStart={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        {/* Содержимое */}
        <div ref={contentRef} className="bottom-sheet-content">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BottomSheet;
